import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Tooltip as UiTooltip } from '../../../../Tooltip';
import styles from './lineChartCard.module.css';
// import ChartDownloadIcon from '../../../../../assets/chartDownloadIcon.svg';
import DownIcon from '../../../../../assets/DownIcon';
import SimplePlusIcon from '../../../../../assets/SimplePlusIcon';

export interface LineConfig {
  key: string;
  name: string;
  color: string;
}

interface LineChartCardProps {
  title: string;
  data: Record<string, any>[];
  lines: LineConfig[];
  xAxisKey: string;
  filterLabel?: string;
  yTickFormatter?: (value: number) => string;
  isLoading?: boolean;
  hasError?: boolean;
  onMonthClick?: (monthLabel: string, metric: 'total' | 'count', visibleKeys: string[]) => void;
}

const METRICS = [
  { label: 'Audience Volume', suffix: 'total' },
  { label: 'Queries Executed', suffix: 'count' },
] as const;

const ErrorState: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 160, gap: 6 }}>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="1.5"/>
      <path d="M12 8v4.5" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="12" cy="16" r="0.875" fill="#F59E0B"/>
    </svg>
    <span style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', lineHeight: 1.5 }}>
      Data unavailable.<br/>Please try refreshing.
    </span>
  </div>
);

const EmptyState: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 160, gap: 6 }}>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#CBD5E1" strokeWidth="1.5"/>
      <path d="M8 12h8" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
    <span style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', lineHeight: 1.5 }}>
      No data available for the selected period.
    </span>
  </div>
);

const LineChartCard: React.FC<LineChartCardProps> = ({
  title,
  data,
  lines,
  xAxisKey,
  filterLabel,
  yTickFormatter,
  isLoading,
  hasError,
  onMonthClick,
}) => {
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  const [metric, setMetric] = useState<'total' | 'count'>('total');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const colorKeyRef = useRef<HTMLDivElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const updateFades = useCallback(() => {
    const el = colorKeyRef.current;
    if (!el) return;
    setFadeLeft(el.scrollLeft > 4);
    setFadeRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => { updateFades(); }, [lines, hiddenKeys, updateFades]);

  const toggleLine = (key: string) => {
    setHiddenKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const visibleLines = lines.filter(l => !hiddenKeys.has(l.key));
  const selectedMetricLabel = METRICS.find(m => m.suffix === metric)?.label ?? 'Select to plot';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        <div className={styles.headerActions}>
          <div className={styles.dropdownWrapper} ref={dropdownRef}>
            <button className={styles.dropdown} onClick={() => setDropdownOpen(o => !o)}>
              <span>{selectedMetricLabel}</span>
              <DownIcon width={10} height={6} style={{ color: '#092538' }} />
            </button>
            {dropdownOpen && (
              <div className={styles.dropdownMenu}>
                {METRICS.map(m => (
                  <button
                    key={m.suffix}
                    className={`${styles.dropdownMenuItem} ${metric === m.suffix ? styles.dropdownMenuItemActive : ''}`}
                    onClick={() => { setMetric(m.suffix); setDropdownOpen(false); }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* <button className={styles.downloadBtn} title="Download">
            <img src={ChartDownloadIcon} width={26} height={26} alt="" />
          </button> */}
        </div>
      </div>

      <div className={styles.divider} />

      {isLoading ? (
        <div className={styles.shimmerArea}>
          <div className={`${styles.shimmerBlock} ${styles.shimmerChart}`} />
          <div className={styles.shimmerLegendRow}>
            {[1, 2, 3].map(i => (
              <div key={i} className={`${styles.shimmerBlock} ${styles.shimmerPill}`} />
            ))}
          </div>
        </div>
      ) : hasError ? (
        <ErrorState />
      ) : data.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Chart */}
          <div className={styles.chartArea}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
                style={onMonthClick ? { cursor: 'pointer' } : undefined}
                onClick={(chartData: any) => {
                  if (onMonthClick && chartData?.activeLabel) {
                    onMonthClick(String(chartData.activeLabel), metric, visibleLines.map(l => l.key));
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" />
                <XAxis
                  dataKey={xAxisKey}
                  axisLine={false}
                  tickLine={false}
                  tick={onMonthClick
                    ? (props: any) => {
                        const { x, y, payload } = props;
                        return (
                          <g transform={`translate(${x},${y})`} style={{ cursor: 'pointer' }} onClick={() => onMonthClick(payload.value, metric, visibleLines.map(l => l.key))}>
                            <text x={0} y={0} dy={12} textAnchor="middle" fill="#0D6AB3" fontSize={10} fontWeight={700}>
                              {payload.value}
                            </text>
                          </g>
                        );
                      }
                    : { fontSize: 10, fill: '#64748b' }
                  }
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  width={45}
                  tickFormatter={yTickFormatter}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 10,
                    borderRadius: 4,
                    border: '1px solid #e2e8f0',
                  }}
                />
                {visibleLines.map((line) => (
                  <Line
                    key={`${line.key}_${metric}`}
                    type="monotone"
                    dataKey={`${line.key}_${metric}`}
                    name={line.name}
                    stroke={line.color}
                    strokeWidth={2}
                    dot={{ r: 3, fill: line.color, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {onMonthClick && (
            <div style={{ padding: '0 var(--space-2sm) 2px', fontSize: 10, color: '#6f7a85' }}>
              Click a month to view daily breakdown
            </div>
          )}

          {/* Color key with scroll fades */}
          <div className={styles.legend}>
            {filterLabel && (
              <span className={styles.filterPill}>{filterLabel}</span>
            )}
            <div className={styles.colorKeyWrapper}>
              {fadeLeft && <div className={`${styles.colorKeyFade} ${styles.colorKeyFadeLeft}`} />}
              {fadeRight && <div className={`${styles.colorKeyFade} ${styles.colorKeyFadeRight}`} />}
              <div
                className={styles.colorKeyRow}
                ref={colorKeyRef}
                onScroll={updateFades}
              >
                {lines.map(line => {
                  const visible = !hiddenKeys.has(line.key);
                  return (
                    <UiTooltip key={line.key} text={visible ? `Hide ${line.name}` : `Show ${line.name}`} placement="top">
                      <button
                        className={`${styles.colorKeyPill} ${!visible ? styles.colorKeyPillHidden : ''}`}
                        onClick={() => toggleLine(line.key)}
                      >
                        <span className={styles.colorKeyDot} style={{ backgroundColor: line.color }} />
                        <span className={styles.colorKeyName}>{line.name}</span>
                        {visible ? (
                          <span className={styles.colorKeyPlusIcon}>
                            <SimplePlusIcon color="#64748b" size={8} />
                          </span>
                        ) : (
                          <SimplePlusIcon color="var(--Primary-Color-Shade-1)" size={8} />
                        )}
                      </button>
                    </UiTooltip>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LineChartCard;
